import { Ticket } from "../model/Ticket";
import { Observable, Subject } from "rxjs";
import { TicketCategoryEnum, TicketSeverityEnum, TicketStatusEnum } from "../types";
import { TicketRepositoryInterface } from "./TicketRepositoryInterface";
import { ConstantsService } from "../../../constants.service";
import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { SessionService } from "../../../session.service";
import { map } from "rxjs/operators";
import { OperatorRepositoryInterface } from "./OperatorRepositoryInterface";

type TicketResponse = {
  "ticketId": string,
  "subject": string,
  "clientId": string,
  "category": TicketCategoryEnum,
  "urgency": TicketSeverityEnum,
  "description": string,
  "email": string,
  "status": TicketStatusEnum,
  "createdAt": string,
  "updatedAt": string,
  "closedAt": string|undefined,
  "internalNote": string,
  "folderName": string,
  "attachments": string[],
  "userId": string,
  "operatorAssignedId": null
}

type TicketsResponse = TicketResponse[];

@Injectable({
  providedIn: 'root'
})
export class TicketRepositoryApi implements TicketRepositoryInterface {
  private readonly apiUrl: string = '';
  private operators: { [operatorId: string]: string } = {};

  constructor(
    private readonly constantsService: ConstantsService,
    private readonly httpClient: HttpClient,
    private readonly session: SessionService,
    @Inject('OperatorRepositoryInterface') private readonly operatorRepository: OperatorRepositoryInterface,
  ) {
    this.apiUrl = this.constantsService.getConstant('apiUrl');

    this.operatorRepository.findAll().subscribe(operators => {
      operators.forEach(operator => {
        this.operators[operator.id] = operator.name;
      });
    })
  }

  closeTicket(ticket: Ticket, closeNote: string, successfully: boolean): Observable<Ticket> {
    const subject = new Subject<Ticket>();

    this.httpClient.post<void>(`${this.apiUrl}/user/support/api/v1/tickets/${ticket.id}/close`, {
      closeNote,
      successfully
    }, {
      withCredentials: true
    }).subscribe({
      next: () => {
        ticket.status = TicketStatusEnum.CLOSED;
        ticket.closedAt = new Date();

        subject.next(ticket);
      },
      error: (error) => {
        console.error('Error closing ticket:', error);
        subject.error(error);
      },
      complete: () => {
        subject.complete();
      }
    });

    return subject.asObservable();
  }

  assignTicket(ticket: Ticket, assignee: string, notes: string): Observable<Ticket> {
    const subject = new Subject<Ticket>();

    this.httpClient.post<void>(`${this.apiUrl}/user/support/api/v1/tickets/${ticket.id}/assign`, {
      operatorId: assignee,
      notes
    }, {
      withCredentials: true
    }).subscribe({
      next: () => {
        ticket.operatorId = assignee;
        ticket.status = TicketStatusEnum.IN_PROGRESS;
        ticket.addNote(notes, this.session.getValue('name'));

        subject.next(ticket);
      },
      error: (error) => {
        console.error('Error assigning ticket:', error);
        subject.error(error);
      }
    });

    return subject.asObservable();
  }

  addTicketNote(ticket: Ticket, notes: string): Observable<Ticket> {
    const subject = new Subject<Ticket>();

    this.httpClient.post<void>(`${this.apiUrl}/user/support/api/v1/tickets/${ticket.id}/notes`, {
      notes
    }, {
      withCredentials: true
    }).subscribe({
      next: () => {
        ticket.addNote(notes, this.session.getValue('name'));
        subject.next(ticket);
      },
      error: (error) => {
        console.error('Error adding ticket note:', error);
        subject.error(error);
      },
      complete: () => {
        subject.complete();
      }
    });

    return subject.asObservable();
  }

  create(ticket: Ticket): Observable<Ticket> {
    const subject = new Subject<Ticket>();
    this.httpClient.post<{ ticketId: string }>(`${this.apiUrl}/user/support/api/v1/tickets`, ticket, {
      withCredentials: true
    }).subscribe({
        next: (ticketFromServer) => {
          ticket.id = ticketFromServer.ticketId;
          ticket.createdAt = new Date();

          subject.next(ticket);

          if (ticket.attachments?.length) {
            this.saveTicketAttachments(ticket).subscribe({
              next: () => {
                ticket.attachments = [];
              },
              error: (error) => {
                console.error('Error uploading attachments:', error);
                subject.error(error);
              },
              complete: () => {
                subject.complete();
              }
            });
          }
        },
        error: (error) => {
          console.error('Error creating ticket:', error);
          subject.error(error);
        }
    });

    return subject.asObservable();
  }

  private filesArrayToFileList(files: File[]) {
    const dataTransfer = new DataTransfer();
    files.forEach(file => dataTransfer.items.add(file));
    return dataTransfer.files;
  }

  saveTicketAttachments(ticket: Ticket): Observable<void> {
    const form = document.createElement('form');
    form.enctype = 'multipart/form-data';
    form.method = 'POST';

    const ticketIdInput = document.createElement('input');
    ticketIdInput.type = 'hidden';
    ticketIdInput.name = 'ticketId';
    ticketIdInput.value = ticket.id;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.name = 'file';
    fileInput.multiple = true;
    fileInput.files = this.filesArrayToFileList(ticket.attachments || []);

    form.appendChild(ticketIdInput);
    form.appendChild(fileInput);

    return this.httpClient.post<void>(this.apiUrl + '/user/support/api/v1/tickets/upload', new FormData(form), {
      withCredentials: true
    });
  }

  findByEmail(email: string): Observable<Ticket[]> {
    return this.httpClient.get<TicketsResponse>(`${this.apiUrl}/user/support/api/v1/tickets?email=${email}`, {
      withCredentials: true
    }).pipe(
      map((tickets: TicketsResponse) => this.parseTicketsResponse(tickets))
    );
  }

  findById(id: string): Observable<Ticket> {
    return this.httpClient.get<TicketResponse>(`${this.apiUrl}/user/support/api/v1/tickets/${id}`, {
      withCredentials: true
    }).pipe(
      map((ticket: TicketResponse) => this.parseTicketResponse(ticket))
    );
  }

  findByOperatorId(operatorId: string): Observable<Ticket[]> {
    // return this.httpClient.get<TicketsResponse>(`${this.apiUrl}/user/support/api/v1/tickets?operatorId=${operatorId}`, {
    return this.httpClient.get<TicketsResponse>(`${this.apiUrl}/user/support/api/v1/tickets/byOperatorId`, {
      withCredentials: true
    }).pipe(
      map((tickets: TicketsResponse) => this.parseTicketsResponse(tickets))
    );
  }

  private parseTicketsResponse(tickets: TicketsResponse): Ticket[] {
    return tickets.map(ticket => this.parseTicketResponse(ticket));
  }

  private parseTicketResponse(ticket: TicketResponse): Ticket {
    const parsedTicket = new Ticket(
      ticket.ticketId,
      ticket.category,
      ticket.subject,
      ticket.description,
      new Date(ticket.createdAt),
      ticket.status,
      ticket.userId,
      ticket.email,
      ticket.email, // TODO: Use userName instead of email
      ticket.clientId,
      ticket.email, // TODO: Use clientBusinessName instead of email,
      ticket.urgency,
      ticket.operatorAssignedId || '',
      ticket.operatorAssignedId ? this.getOperatorName(ticket.operatorAssignedId) : 'N/A',
      ticket.internalNote || '',
      [],
      ticket.closedAt ? new Date(ticket.closedAt) : undefined,
    );

    if (ticket.attachments) {
      parsedTicket.setAttachmentsSignedUrls(ticket.attachments);
    }

    return parsedTicket;
  }

  getOperatorName(operatorId: string): string {
    return this.operators[operatorId];
  }

  findBySeverity(severity: TicketSeverityEnum): Observable<Ticket[]> {
    return this.httpClient.get<TicketsResponse>(`${this.apiUrl}/user/support/api/v1/tickets?urgency=${severity}`, {
      withCredentials: true
    }).pipe(
      map((tickets: TicketsResponse) => this.parseTicketsResponse(tickets))
    );
  }

  findByStatus(status: TicketStatusEnum): Observable<Ticket[]> {
    return this.httpClient.get<TicketsResponse>(`${this.apiUrl}/user/support/api/v1/tickets?status=${status}`, {
      withCredentials: true
    }).pipe(
      map((tickets: TicketsResponse) => this.parseTicketsResponse(tickets))
    );
  }

  update(ticket: Ticket): Observable<Ticket> {
    return this.httpClient.put<Ticket>(`${this.apiUrl}/user/support/api/v1/tickets/${ticket.id}`, ticket, {
      withCredentials: true
    });
  }
}
